import React, { useState, useEffect } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Modal,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const CohortCharts = () => {
  const [dateRange, setDateRange] = useState("Last 30 Days");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);

  // State for fetched data
  const [fetchedRetentionChartData, setFetchedRetentionChartData] = useState(null);
  const [fetchedPredictiveRetentionChartData, setFetchedPredictiveRetentionChartData] = useState(null);
  const [fetchedCohortShadingChartData, setFetchedCohortShadingChartData] = useState(null);
  const [fetchedEngagementChartData, setFetchedEngagementChartData] = useState(null);
  const [fetchedRevenueChartData, setFetchedRevenueChartData] = useState(null);
  const [fetchedChurnChartData, setFetchedChurnChartData] = useState(null);

  useEffect(() => {
    const fetchCohortData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
        console.log("Using API URL:", apiUrl);
        const token = localStorage.getItem("token"); // Assuming the token is stored in localStorage
        const response = await fetch(`${apiUrl}/api/csv/cohorts`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`, // Include the Bearer token
            "Content-Type": "application/json",
          },
        });      
        if (response.ok) {
          const data = await response.json();
          const { cohorts } = data;

          setFetchedRetentionChartData(cohorts.retention_chart);
          setFetchedPredictiveRetentionChartData(cohorts.predictive_retention_chart);
          setFetchedCohortShadingChartData(cohorts.cohort_shading_chart);
          setFetchedEngagementChartData(cohorts.engagement_chart);
          setFetchedRevenueChartData(cohorts.revenue_chart);
          setFetchedChurnChartData(cohorts.churn_chart);
        } else {
          console.error("Failed to fetch cohort data");
        }
      } catch (error) {
        console.error("Error fetching cohort data:", error);
      }
    };

    fetchCohortData();
  }, []);

  const handleDateChange = (event) => {
    setDateRange(event.target.value);
  };

  const handleChartClick = (chart) => {
    setSelectedChart(chart);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedChart(null);
  };

  // Dummy Chart Data
  const retentionChartData = fetchedRetentionChartData || {
    labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
    datasets: [
      {
        label: "Retention Rate (%)",
        data: [90, 75, 60, 50],
        borderColor: "#0072E3",
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: "#0072E3",
        pointRadius: 4,
      },
    ],
  };

  const predictiveRetentionChartData = fetchedPredictiveRetentionChartData || {
    labels: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6"],
    datasets: [
      {
        label: "Historical Retention",
        data: [90, 75, 60, 50],
        borderColor: "#0072E3",
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: "#0072E3",
        pointRadius: 4,
      },
      {
        label: "Predicted Retention",
        data: [50, 45, 40, 35, 30, 25],
        borderColor: "#FFA726",
        borderDash: [5, 5],
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: "#FFA726",
        pointRadius: 4,
      },
    ],
  };

  const cohortShadingChartData = fetchedCohortShadingChartData || {
    labels: ["Cohort A", "Cohort B", "Cohort C", "Cohort D"],
    datasets: [
      {
        label: "Retention Intensity",
        data: [90, 75, 60, 50],
        backgroundColor: [
          "rgba(0, 114, 227, 0.9)",
          "rgba(0, 114, 227, 0.7)",
          "rgba(0, 114, 227, 0.5)",
          "rgba(0, 114, 227, 0.3)",
        ],
      },
    ],
  };

  const engagementChartData = fetchedEngagementChartData || {
    labels: ["Cohort A", "Cohort B", "Cohort C", "Cohort D"],
    datasets: [
      {
        label: "Engagement (%)",
        data: [80, 70, 60, 50],
        backgroundColor: "#1E88E5",
      },
    ],
  };

  const revenueChartData = fetchedRevenueChartData || {
    labels: ["Cohort A", "Cohort B", "Cohort C", "Cohort D"],
    datasets: [
      {
        label: "Revenue ($)",
        data: [5000, 4000, 3000, 2000],
        backgroundColor: "#43A047",
      },
    ],
  };

  const churnChartData = fetchedChurnChartData || {
    labels: ["Cohort A", "Cohort B", "Cohort C", "Cohort D"],
    datasets: [
      {
        label: "Churn Rate (%)",
        data: [10, 20, 15, 25],
        backgroundColor: "#E53935",
      },
    ],
  };

  const annotations = {
    "Retention Over Time": "Retention drops sharply after Week 3.",
    "Predictive Retention Curves": "Predicted retention suggests a continued decline in Weeks 5 and 6.",
    "Retention by Cohort (Shaded by Intensity)": "Cohort A has the highest retention intensity.",
    "Engagement by Cohort": "Engagement decreases consistently across cohorts.",
    "Revenue by Cohort": "Cohort A generates the most revenue.",
    "Churn Rate by Cohort": "Cohort D has the highest churn rate.",
  };

  const tableData = [
    { cohort: "Cohort A", retention: "90%", churn: "10%", revenue: "$5,000" },
    { cohort: "Cohort B", retention: "75%", churn: "20%", revenue: "$4,000" },
    { cohort: "Cohort C", retention: "60%", churn: "15%", revenue: "$3,000" },
    { cohort: "Cohort D", retention: "50%", churn: "25%", revenue: "$2,000" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f4f5f7" }}>
      <Box sx={{ padding: "32px" }}>
        {/* Header Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }} gutterBottom>
              Cohort Analytics
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Explore retention, engagement, revenue, and churn trends across cohorts.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Date Range</InputLabel>
            <Select value={dateRange} onChange={handleDateChange} label="Date Range">
              <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
              <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
              <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Charts Section */}
        <Grid container spacing={4}>
          {[{
            title: "Retention Over Time",
            data: retentionChartData,
            type: "line"
          }, {
            title: "Predictive Retention Curves",
            data: predictiveRetentionChartData,
            type: "line"
          }, {
            title: "Retention by Cohort (Shaded by Intensity)",
            data: cohortShadingChartData,
            type: "bar"
          }, {
            title: "Engagement by Cohort",
            data: engagementChartData,
            type: "bar"
          }, {
            title: "Revenue by Cohort",
            data: revenueChartData,
            type: "bar"
          }, {
            title: "Churn Rate by Cohort",
            data: churnChartData,
            type: "bar"
          }].map((chart, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  padding: 3,
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  height: "400px", // Adjusted height to fit grid
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
                onClick={() => handleChartClick(chart)}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                  {chart.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#777", mb: 2 }}>
                  {annotations[chart.title]}
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  {chart.type === "line" ? (
                    <Line
                      data={chart.data}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false, // Allows the chart to adjust to the container height
                        plugins: { legend: { position: "top" } },
                        scales: { x: { grid: { display: false } }, y: { grid: { color: "#e0e0e0" } } },
                      }}
                    />
                  ) : (
                    <Bar
                      data={chart.data}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: "top" } },
                        scales: { x: { grid: { display: false } }, y: { grid: { color: "#e0e0e0" } } },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Modal for Drill Down */}
        <Modal open={modalOpen} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
            }}
          >
            {selectedChart && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {selectedChart.title} (Detailed View)
                </Typography>
                {selectedChart.type === "line" ? (
                  <Line
                    data={selectedChart.data}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: "top" } },
                      scales: { x: { grid: { display: false } }, y: { grid: { color: "#e0e0e0" } } },
                    }}
                  />
                ) : (
                  <Bar
                    data={selectedChart.data}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: "top" } },
                      scales: { x: { grid: { display: false } }, y: { grid: { color: "#e0e0e0" } } },
                    }}
                  />
                )}
              </>
            )}
          </Box>
        </Modal>

        {/* Table Section */}
        <Box sx={{ mt: 5 }}>
          <Box
            sx={{
              backgroundColor: "#fff",
              padding: 3,
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
              Cohort Comparison Table
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {Object.keys(tableData[0]).map((header) => (
                      <TableCell key={header} sx={{ fontWeight: 600, color: "#555" }}>
                        {header.charAt(0).toUpperCase() + header.slice(1)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row, idx) => (
                    <TableRow key={idx}>
                      {Object.values(row).map((value, colIdx) => (
                        <TableCell key={colIdx} sx={{ color: "#333" }}>
                          {value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CohortCharts;
